import { FooterSEOContent } from "@faire/retailer-visitor-shared/components/Footer/FooterSEOContent";
import { FaireHelmet } from "@faire/retailer-visitor-shared/components/Helmet/index";
import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { getIsRobot } from "@faire/retailer-visitor-shared/serialized-data/getIsRobot";
import { useOpenWithFaireApplicationStatus } from "@faire/retailer-visitor-shared/stores/domain/OpenWithFaire/useOpenWithFaireApplicationStatus";
import { ChapterBody } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChapterBody";
import { ChapterColumnSEOWrapper } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChapterColumnSEOWrapper";
import { ChaptersNavSection } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChaptersNavSection";
import { ExploreOtherGuides } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ExploreOtherGuides";
import { GuideBodyRobots } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/GuideBodyRobots";
import { GuidePageHeader } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/GuidePageHeader";
import { JumpToTheNextGuide } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/JumpToTheNextGuide";
import { PaymentTermsUpsell } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/PaymentTermsUpsell";
import { GuideRowSection } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/styles";
import { useGetActiveChapterHash } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGetActiveChapterHash";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import type { FaireAcademyChapter } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { Column, HorizontalLine } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useQuery as useFaireAcademyGuideChaptersQuery } from "@faire/web-api--source/endpoints/www/api/faire-academy/guideIdentifier/chapterIdentifier/get-hooks";
import { trackOpenWithFaireFaireAcademyGuideChapterView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademyGuideChapter";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import openWithFaireAcademy from "@faire/web-api--source/routes/www/open-with-faire/academy";
import React, { useRef, useEffect, useState } from "react";
import { Redirect, useLocation, useHistory } from "react-router-dom";
import styled from "styled-components";

export const GuidePageTemplate: React.FC = () => {
  const [{ guide }] = useGuideStore(["guide"]);
  const location = useLocation();
  const chapterHash = useGetActiveChapterHash();
  const chapterIndex = guide.chapters.findIndex(
    (chapter) => chapter.titleHash === chapterHash
  );
  const chapter = guide.chapters[chapterIndex];
  const firstChapter = guide.chapters[0];

  if (chapter === undefined && firstChapter !== undefined) {
    return <Redirect to={{ ...location, hash: firstChapter.titleHash }} />;
  }

  if (chapter === undefined) {
    return <Redirect to={openWithFaireAcademy()} />;
  }

  return <GuidePageContent chapter={chapter} chapterIndex={chapterIndex} />;
};

const GuidePageContent: React.FC<{
  chapter: FaireAcademyChapter;
  chapterIndex: number;
}> = ({ chapter, chapterIndex }) => {
  const isRobot = getIsRobot();
  const [{ guide }, setGuideStore] = useGuideStore(["guide"]);
  const { data: guideDataResponse } = useFaireAcademyGuideChaptersQuery(
    guide.id,
    chapter.id
  );
  const { canApply } = useOpenWithFaireApplicationStatus();
  const nextChapter = guide.chapters[chapterIndex + 1];
  const isLastChapter = chapterIndex === guide.chapters.length - 1;

  useEffect(() => {
    setGuideStore({ guideBackendData: guideDataResponse?.guide });
  }, [guideDataResponse, setGuideStore]);

  return (
    <>
      <FaireHelmet title={guide.titleTag} />
      <Column align="center">
        <GuidePageHeader
          guide={{ ...guide, likes: guideDataResponse?.guide?.likes }}
        />
        {isRobot ? (
          <GuideBodyRobots />
        ) : (
          <GuideBody chapter={chapter} nextChapter={nextChapter} />
        )}
        {isLastChapter ? <JumpToTheNextGuide currentGuide={guide} /> : null}
        <ExploreOtherGuides
          currentGuide={guide}
          isLastChapter={isLastChapter}
        />
        {canApply ? <PaymentTermsUpsell /> : <HorizontalLine />}
      </Column>
      <FooterSEOContent />
    </>
  );
};

const GuideBody: React.FC<{
  chapter: FaireAcademyChapter;
  nextChapter: FaireAcademyChapter | undefined;
}> = ({ chapter, nextChapter }) => {
  const [{ guide, guideBackendData }] = useGuideStore([
    "guide",
    "guideBackendData",
  ]);
  const chapterHash = chapter.titleHash;
  const chaptersBackendData = guideBackendData?.chapters;
  const [readChapters, setReadChapters] = useState<
    Set<keyof typeof IFaireAcademyChapter.ChapterIdentifier | undefined>
  >(new Set());
  useEffect(() => {
    if (chaptersBackendData === undefined) {
      return;
    }
    setReadChapters(
      new Set(
        chaptersBackendData
          .filter((chapter) => chapter.is_read)
          .map((chapter) => chapter.identifier)
      )
    );
  }, [chaptersBackendData]);

  const handleReadChapter = (
    chapterId: IFaireAcademyChapter.ChapterIdentifier
  ) => {
    setReadChapters(
      (prevReadChapters) => new Set([...prevReadChapters, chapterId])
    );
  };

  const { pageState } = useGetFaireAcademyPageState();
  const history = useHistory();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackOpenWithFaireFaireAcademyGuideChapterView(
      pageState,
      guide.title,
      chapterHash ?? ""
    );
  }, [pageState, guide.title, chapterHash]);

  useEffect(() => {
    const stopListener = history.listen(() => {
      ref.current?.scrollIntoView();
    });
    // Scroll to top of chapter on chapter change
    return () => {
      stopListener();
      // Need to scroll back to top on unmount so we start at the top on return
      getWindowOrThrow().scrollTo?.({
        top: 0,
      });
    };
  }, [history]);

  return (
    <>
      <div ref={ref} />
      <GuideChaptersSection gap={{ tabletAndAbove: "10x", mobileAndAbove: 0 }}>
        <ChaptersNavSection
          guideTitle={guide.title}
          chapters={guide.chapters}
          readChapters={readChapters}
        />
        <ChapterColumnSEOWrapper>
          <ChapterBody
            chapter={chapter}
            nextChapter={nextChapter}
            readChapters={readChapters}
            handleReadChapter={handleReadChapter}
          />
        </ChapterColumnSEOWrapper>
      </GuideChaptersSection>
    </>
  );
};

const GuideChaptersSection = styled(GuideRowSection)`
  padding-top: ${getSpacing("5x")};
  padding-bottom: ${getSpacing("5x")};

  ${responsiveMediaQueries.mobile} {
    flex-direction: column;
    padding-top: ${getSpacing("3x")};
    padding-bottom: ${getSpacing("3x")};
  }
`;
